/**
 * DecisionEase — Settings API Client
 */
import { authFetch } from "./api";

const API_BASE = "/api/settings";

export interface Settings {
  display_name: string;
  email: string | null;
  avatar_url: string | null;
  tone_preference: string;
  theme: string;
  flow_mode: boolean;
  quiet_hours_start: string | null;
  quiet_hours_end: string | null;
  // Working-hours window for AI Time Box / schedule generation.
  // Local-clock hour, 0–23 inclusive for start, 1–24 for end.
  day_start_hour: number;
  day_end_hour: number;
  // Energy-zone personalization (read by BOTH schedule engines via
  // backend/services/energy_zones.py): chronotype is the coarse setting,
  // the peak window the explicit override (local-clock hours, start < end).
  chronotype: string | null;
  peak_start_hour: number | null;
  peak_end_hour: number | null;
  agent_autonomy: Record<string, string>;
  google_connected: boolean;
}

async function handleResponse(res: Response): Promise<Settings> {
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Settings update failed");
  }
  return res.json();
}

export async function getSettings(): Promise<Settings> {
  const res = await authFetch(API_BASE);
  return handleResponse(res);
}

export async function updateEnergy(data: {
  chronotype?: string | null;
  peak_start_hour?: number | null;
  peak_end_hour?: number | null;
}): Promise<Settings> {
  const res = await authFetch(`${API_BASE}/energy`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export async function updateProfile(data: {
  display_name?: string;
  avatar_url?: string;
  timezone?: string;
}): Promise<Settings> {
  const res = await authFetch(`${API_BASE}/profile`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

// Detect the browser's IANA timezone and persist it as the user's SSOT, so
// the schedule generator places AI Time Box blocks inside the local working
// window (ISSUE-012). Best-effort + idempotent; failures are non-fatal.
export async function syncBrowserTimezone(): Promise<void> {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!tz) return;
    const key = "de_synced_timezone";
    if (typeof window !== "undefined" && localStorage.getItem(key) === tz) {
      return; // already synced this zone this device — avoid a PATCH per load
    }
    await updateProfile({ timezone: tz });
    if (typeof window !== "undefined") localStorage.setItem(key, tz);
  } catch {
    // non-fatal: scheduler falls back to UTC
  }
}

export async function updateTone(tone_preference: string): Promise<Settings> {
  const res = await authFetch(`${API_BASE}/tone`, {
    method: "PATCH",
    body: JSON.stringify({ tone_preference }),
  });
  return handleResponse(res);
}

export async function updateTheme(theme: string): Promise<Settings> {
  const res = await authFetch(`${API_BASE}/theme`, {
    method: "PATCH",
    body: JSON.stringify({ theme }),
  });
  return handleResponse(res);
}

export async function updateAutonomy(
  agent_autonomy: Record<string, string>
): Promise<Settings> {
  const res = await authFetch(`${API_BASE}/autonomy`, {
    method: "PATCH",
    body: JSON.stringify({ agent_autonomy }),
  });
  return handleResponse(res);
}

export async function updateQuietHours(
  start: string | null,
  end: string | null
): Promise<Settings> {
  const res = await authFetch(`${API_BASE}/quiet-hours`, {
    method: "PATCH",
    body: JSON.stringify({
      quiet_hours_start: start,
      quiet_hours_end: end,
    }),
  });
  return handleResponse(res);
}

export async function updateWorkingHours(
  day_start_hour: number,
  day_end_hour: number,
): Promise<Settings> {
  const res = await authFetch(`${API_BASE}/working-hours`, {
    method: "PATCH",
    body: JSON.stringify({ day_start_hour, day_end_hour }),
  });
  return handleResponse(res);
}

export async function updateFlowMode(flow_mode: boolean): Promise<Settings> {
  const res = await authFetch(`${API_BASE}/flow-mode`, {
    method: "PATCH",
    body: JSON.stringify({ flow_mode }),
  });
  return handleResponse(res);
}

// ── AI memory (Phase 11-3.b) ──────────────────────────────
// Backend contract (locked at Phase 11-3 backend slice 634e607):
// - GET    /api/memory/preferences           — flat list of the user's editable promoted-type rows
// - PATCH  /api/memory/preferences/:id       — partial {content?, importance?} (content edit re-embeds)
// - DELETE /api/memory/preferences/:id       — hard delete (204 No Content)
// Editable memory_types: preference / recurring_blocker / decision_rule / recurring_ambition.

export type PreferenceMemoryType =
  | "preference"
  | "recurring_blocker"
  | "decision_rule"
  | "recurring_ambition";

export interface PreferenceMemory {
  id: string;
  memory_type: PreferenceMemoryType;
  content: string;
  content_summary: string;
  importance: number;
  source_agent: string;
  tags: string[];
  memory_metadata: Record<string, unknown>;
  created_at: string | null;
}

const MEMORY_BASE = "/api/memory";

async function handleMemoryListResponse(res: Response): Promise<PreferenceMemory[]> {
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to load memories");
  }
  return res.json();
}

async function handleMemoryRowResponse(res: Response): Promise<PreferenceMemory> {
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Memory update failed");
  }
  return res.json();
}

export async function listPreferenceMemories(): Promise<PreferenceMemory[]> {
  const res = await authFetch(`${MEMORY_BASE}/preferences`);
  return handleMemoryListResponse(res);
}

export async function updatePreferenceMemory(
  id: string,
  body: { content?: string; importance?: number },
): Promise<PreferenceMemory> {
  const res = await authFetch(`${MEMORY_BASE}/preferences/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
  return handleMemoryRowResponse(res);
}

export async function deletePreferenceMemory(id: string): Promise<void> {
  const res = await authFetch(`${MEMORY_BASE}/preferences/${id}`, {
    method: "DELETE",
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Memory delete failed");
  }
}
